<template>
    <cic-popup
        :title="item.profileId ? 'Edit Voicemail Profile' : 'New Voicemail Profile'"
        :saveable="editing"
        @blur="$emit('blur')"
        @input="$emit('input')"
    >
        <div class="item-form">
            <ul>
                <li class="form-label-first">
                    {{ $t('Profile Name') }}
                </li>
                <li>
                    <cic-textbox
                        id="profile_name"
                        :enabled="editing"
                        :value="item.name"
                        :maxlength="63"
                        @input="(val) => updateFieldValue('name', val)"
                    />
                </li>
                <li class="form-label">
                    {{ $t('Description') }}
                </li>
                <li>
                    <cic-textbox
                        id="profile_description"
                        :enabled="editing"
                        :value="item.description"
                        :maxlength="255"
                        style="width: 100%"
                        @input="(val) => updateFieldValue('description', val)"
                    />
                </li>
            </ul>
            <hr />
            <h3>{{ $t('Mailbox Security') }}</h3>
            <ul>
                <li class="form-label-first">
                    {{ $t('Disable Mailbox PIN?') }}
                </li>
                <li class="inline-form-item">
                    <cic-checkbox
                        id="profile_disable_pin"
                        :enabled="editing"
                        :value="item.disablePin"
                        @input="(val) => updateFieldValue('disablePin', val)"
                    />
                    <icon-button icon="help-icon" :title="$t('DESC_MAILBOX_DISABLE_PIN')" style="margin-left: 197px" />
                </li>
                <template v-if="!item.disablePin">
                    <li class="form-label">
                        {{ $t(item.profileId ? 'New PIN' : 'PIN') }}
                    </li>
                    <li class="inline-form-item">
                        <cic-textbox
                            id="profile_pin"
                            :enabled="editing"
                            :value="item.pin"
                            :maxlength="4"
                            :placeholder="'[' + $t(item.profileId ? 'New PIN' : 'PIN') + ']'"
                            type="password"
                            @input="(val) => updateFieldValue('pin', val)"
                        />
                        <icon-button icon="help-icon" :title="$t('DESC_MAILBOX_PIN')" />
                    </li>
                    <li class="form-label">
                        {{ $t('Confirm PIN') }}
                    </li>
                    <li>
                        <cic-textbox
                            id="profile_confirm_pin"
                            :enabled="editing"
                            :value="item.confirmPin"
                            :maxlength="4"
                            :placeholder="'[' + $t('Confirm PIN') + ']'"
                            type="password"
                            @input="(val) => updateFieldValue('confirmPin', val)"
                        />
                    </li>
                </template>
            </ul>
            <hr />
            <h3>{{ $t('Mailbox Introduction Settings') }}</h3>
            <ul>
                <CicRadioGroup
                    id="profile_intro_type"
                    :value="item.introType"
                    :enabled="editing"
                    :options="[
                        { value: 0, label: $t('Play Default Greeting Prompt') },
                        { value: 1, label: $t('Play Personal Greeting Message') },
                    ]"
                    class="vertical"
                    @input="(val) => updateFieldValue('introType', val)"
                />
            </ul>
            <hr />
            <h3>{{ $t('Notification Settings') }}</h3>
            <ul>
                <li class="form-label-first">
                    {{ $t('Email Notification?') }}
                </li>
                <li class="inline-form-item">
                    <cic-checkbox
                        id="profile_email_notification"
                        :enabled="editing"
                        :value="item.emailNotification"
                        @input="(val) => updateFieldValue('emailNotification', val)"
                    />
                    <icon-button
                        icon="help-icon"
                        :title="$t('DESC_MAILBOX_EMAIL_NOTIFICATION')"
                        style="margin-left: 197px"
                    />
                </li>
                <template v-if="item.emailNotification">
                    <li class="form-label">
                        {{ $t('Email Notification Address') }}
                    </li>
                    <li class="inline-form-item">
                        <cic-textbox
                            id="profile_email_address"
                            :enabled="editing"
                            :value="item.emailAddress"
                            :maxlength="64"
                            @input="(val) => updateFieldValue('emailAddress', val)"
                        />
                        <icon-button icon="help-icon" :title="$t('DESC_MAILBOX_EMAIL_ADDRESS')" />
                    </li>
                    <li class="form-label">
                        {{ $t('Email Attachments?') }}
                    </li>
                    <li class="inline-form-item">
                        <cic-checkbox
                            id="profile_email_attachments"
                            :enabled="editing"
                            :value="item.emailAttachments"
                            @input="(val) => updateFieldValue('emailAttachments', val)"
                        />
                        <icon-button
                            icon="help-icon"
                            :title="$t('DESC_MAILBOX_EMAIL_ATTACHMENTS')"
                            style="margin-left: 197px"
                        />
                    </li>
                </template>
            </ul>
            <hr />
            <h3>{{ $t('General Mailbox Settings') }}</h3>
            <ul>
                <li class="form-label-first">
                    {{ $t('Maximum messages') }}
                </li>
                <li class="inline-form-item">
                    <cic-textbox
                        id="profile_max_messages"
                        :enabled="editing"
                        :value="item.maxMessages"
                        :int-only="true"
                        @input="(val) => updateFieldValue('maxMessages', val)"
                    />
                    <icon-button icon="help-icon" :title="$t('DESC_MAILBOX_MAX_MESSAGES')" />
                </li>
                <li class="form-label">
                    {{ $t('Service Type') }}
                </li>
                <li>
                    <cic-select
                        id="profile_service_type"
                        :enabled="editing"
                        :value="item.serviceType"
                        :options="serviceTypes.map((x) => ({ value: x.typeId, label: x.name }))"
                        @input="(val) => updateFieldValue('serviceType', val)"
                    />
                </li>
            </ul>
        </div>
    </cic-popup>
</template>

<script>
    import CicPopup from '@/components/CicPopup';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import CicRadioGroup from '@/components/inputs/CicRadioGroup';
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import IconButton from '@/components/controls/IconButton';

    export default {
        name: 'EditVoicemailProfile',
        components: {
            IconButton,
            CicCheckbox,
            CicPopup,
            CicRadioGroup,
            CicSelect,
            CicTextbox,
        },
        props: {
            item: {
                type: Object,
                required: true,
            },
            serviceTypes: {
                type: Array,
                required: true,
            },
        },
        emits: ['blur', 'input', 'updateFieldValue'],
        data() {
            return {
                editing: false,
            };
        },
        created() {
            this.editing = !this.item.profileId || this.$store.hasRight('EDIT_VOICEMAIL_PROFILES');
        },
        methods: {
            updateFieldValue(field, val) {
                this.$emit('updateFieldValue', field, val);
            },
        },
    };
</script>
